/**
 * SN_SAI_CUS_REG_001 — 거래처 관리 (본사) (2026-09-18, 2차 검토 확정)
 *
 * 왼쪽 거래처 목록(crm_maint_cust) — 거래처코드 · 거래처명 · 배포구분 · AI 사용 · 사용여부. 행을 고르면 오른쪽 폼에 상세 + 접속키 읽기 칸(접속ID · 발급일시 · 차단).
 * 거래처 저장은 툴바 [저장] (JPA). 접속키는 거래처당 하나 — 환경(테스트 · 운영) 개념은 없다. 테스트 기간은 포인트 정책의 무료 줄로.
 * 폼 머리 [키 발급] [비밀번호 교체] 는 중앙 관리 API 다. 차단은 'AI 사용' 체크 해제(2026-10-01) — 값 계산이 중앙에 있어 표에 직접 쓰지 않는다. 비밀번호는 응답으로 한 번만 보인다.
 *
 * 조회조건 · 그리드 컬럼 · 폼 · 팝업 정의는 DB(sys_formctrl · sys_formgridcolumn · sys_popup*, 08_AI_화면설정.sql)에서 온다. 조회는 sys_query sn_sai_cus_reg_001_01.
 * 본 : 사업장등록 SN_MAS_ORG_REG_002 (좌 그리드 · 우 SharedForm)
 */
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import SearchPanel from "@widgets/SearchPanel";
import { callApi, fnGetDataSet, fnSave } from "@shared/api/restApi";
import { getInitFormData } from "@shared/utils/CommonUtils";
import { showDialog } from "@widgets/CommonDialog";
import { global } from "@shared/libs/Global";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnButton from "@shared/components/SnButton";
import SharedForm from "@widgets/SharedForm";
import { menuInfo } from "@shared/type/CommonType";

interface Props {
  Menuprops: menuInfo;
}

const SN_SAI_CUS_REG_001 = forwardRef(({ Menuprops }: Props, ref) => {
  const { userInfo } = global();

  const searchRef = useRef<any>(null);
  const gridRefs = useRef<{ [key: string]: React.RefObject<any> }>({
    grid1: React.createRef(),
  });

  const [formData, setFormData] = useState<any>();
  const [formControlData, setFormControlData] = useState<any[]>([]);
  const [columns, setColums] = useState<any>({
    grid1: [],
  });
  const [dataSources, setDataSources] = useState<any>({
    grid1: [],
  });
  const sharedFormRef1 = useRef<any>(null);
  const [sharedForm, setSharedForm] = useState<any[]>([]);
  const [focusedRowData, setFocusedRowData] = useState<any>();

  useEffect(() => {
    const fetchform = async () => {
      try {
        const rtn: any = await getInitFormData();
        setFormData(rtn.formData);
        setFormControlData(rtn.formControlData);
        setColums({
          grid1: rtn.grid.grid1 || [],
        });
        setSharedForm(rtn.form?.freeform1 || []);
      } catch (error) {}
    };
    fetchform();
  }, []);

  useImperativeHandle(ref, () => ({
    onSearch, onInsert, onDelete, onSave,
  }));

  const onSearch = async () => {
    if (
      gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0
    ) {
      const confirm = await showDialog("confirmCancel", "수정된 내용이 있습니다. 저장하시겠습니까?");
      if (confirm) {
        const isSaved: any = await onSave();
        if (!isSaved) return;
      } else if (confirm === undefined) {
        return;
      }
    }
    try {
      /* 다시 읽기 전에 수정 표시를 지운다. 안 지우면 아니오를 눌러도 "수정 중" 으로 남는다 */
      gridRefs.current["grid1"].current?.resetUpdate?.();
      const params = {
        id: "sn_sai_cus_reg_001_01",
        params: searchRef.current.getFormdata(),
      };
      setFocusedRowData(null);
      const data: any = await fnGetDataSet(params);
      setDataSources({
        grid1: data?.[0] || [],
      });
    } catch (error: any) {}
  };

  const onInsert = async () => {
    try {
      gridRefs.current["grid1"].current?.addRow();
    } catch (error: any) {}
  };
  const onDelete = async () => {
    if (gridRefs.current["grid1"].current?.getDisplayedRowCount() < 1) return;
    const confirm = await showDialog("confirm", "선택한 행을 삭제 표시합니다. 저장하면 지워집니다.");
    if (!confirm) return;
    gridRefs.current["grid1"].current?.deleteSelectedRows();
  };
  const onSave = async () => {
    try {
      gridRefs.current["grid1"].current.stopEditing();
      if (!gridRefs.current["grid1"].current.requiredValuesCheck()) return false;
      const crm_maint_custChanges = gridRefs.current["grid1"].current.getChange();
      if (crm_maint_custChanges.modifiedRowcount === 0) {
        await showDialog("alter", "변경된 내용이 없습니다.");
        return false;
      }
      const saveData: any = {
        crm_maint_cust: crm_maint_custChanges,
      };
      const ok = await fnSave("SN_SAI_CUS_REG_001_SAVE_01", saveData);
      if (!ok) return false;
      gridRefs.current["grid1"].current.resetUpdate();
      await onSearch();
      return true;
    } catch (error: any) {
      gridRefs.current["grid1"].current?.resetUpdate(true);
      return false;
    }
  };
  /** 행추가 기본값 — 회사. 배포구분 SAAS · 유지보수 Y · AI 사용 Y · 사용여부 Y 는 cd_value. 고정값(사용여부 등)은 sys_formgridcolumn.cd_value 가 넣는다 */
  const onBeforeAddCust = (): any => {
    return { cd_firm: userInfo.cd_firm };
  };
  /** 버튼 공통 — 저장된 거래처 한 줄이 골라져 있어야 한다 */
  const selectedCust = async () => {
    const row = gridRefs.current["grid1"].current?.focusedRowNode()?.data;
    if (!row || !row.cd_cust) {
      await showDialog("alter", "거래처를 먼저 선택해 주세요.");
      return null;
    }
    if (gridRefs.current["grid1"].current.getChange().modifiedRowcount > 0) {
      await showDialog("alter", "수정 중인 내용을 먼저 저장해 주세요.");
      return null;
    }
    return row;
  };

  /** [키 발급] — 접속 ID 는 거래처 코드, 비밀번호는 중앙이 만든다. 비밀번호는 이 안내창에서만 보인다 */
  const onIssue = async () => {
    const row = await selectedCust();
    if (!row) return;
    if (row.tm_ai_issue) {
      await showDialog("alter", `${row.cd_cust} 는 이미 접속키가 있습니다. 새 비밀번호가 필요하면 [비밀번호 교체] 를 쓰세요.`);
      return;
    }
    const confirm = await showDialog("confirm", `${row.nm_cust || row.cd_cust} 의 접속키를 발급합니다. 비밀번호는 한 번만 보입니다.`);
    if (!confirm) return;
    const result = await callApi("SN_SAI_CUS_REG_001_btnIssue", { cdCust: row.cd_cust });
    if (!result?.status) return;
    const d = result.data || {};
    await showDialog("alter", `발급했습니다. 비밀번호는 지금 한 번만 보입니다. 거래처 SNOTES 의 AI 센터 연결 화면에 넣어 주세요.\n\n거래처 : ${d.cdCust}\n접속 ID : ${d.clientId}\n비밀번호 : ${d.clientSecret}`);
    await onSearch();
  };

  const onRotate = async () => {
    const row = await selectedCust();
    if (!row) return;
    if (!row.tm_ai_issue) {
      await showDialog("alter", "아직 접속키가 없습니다. [키 발급] 을 먼저 하세요.");
      return;
    }
    const confirm = await showDialog("confirm", `${row.nm_cust || row.cd_cust} 의 비밀번호를 새로 만듭니다. 옛 비밀번호는 바로 쓸 수 없습니다.`);
    if (!confirm) return;
    const result = await callApi("SN_SAI_CUS_REG_001_btnRotate", { cdCust: row.cd_cust });
    if (!result?.status) return;
    const d = result.data || {};
    await showDialog("alter", `새 비밀번호는 지금 한 번만 보입니다.\n\n접속 ID : ${d.clientId}\n비밀번호 : ${d.clientSecret}`);
    await onSearch();
  };

  /** 왼쪽에서 행을 고르면 오른쪽 폼에 그 행. 폼에서 고친 값은 gridref 로 그리드 행에 되돌아간다 — 저장은 그리드 변경으로 간다 */
  const onFocusedRowChanged = useCallback(
    (row: any | null) => {
      setFocusedRowData(row);
    },
    [focusedRowData]
  );
  const grids = useMemo(
    () => ({
      grid1: {
        columns: columns.grid1,
        dataSource: dataSources.grid1,
        events: {
          onBeforeAddRow: onBeforeAddCust,
          onFocusedRowChanged: onFocusedRowChanged
        },
      },
    }),
    [columns, dataSources]
  );

  return (
    <div className="h-full flex flex-col">
      <div className="mb-1">
        <SearchPanel ref={searchRef} data={formData} controls={formControlData} />
      </div>
      <div className="w-full flex h-full min-h-0 space-x-1.5">
        <SnResizeBar direction={"width"} id="panelList" orgSize="23.5%">
          <SnGrid
            ref={gridRefs.current["grid1"]}
            columnConfig={grids.grid1.columns}
            rowData={grids.grid1.dataSource}
            etcProps={{ rowSelection: "single", title: "거래처", isEditable: false }}
            events={grids.grid1.events}
          />
        </SnResizeBar>
        <SnResizeBar direction={"width"} id="panelDetail" orgSize="76.5%">
          <SharedForm
            ref={sharedFormRef1}
            data={focusedRowData}
            controls={sharedForm}
            gridref={gridRefs.current["grid1"]}
            title="상세정보"
            header={
              <div className="flex justify-end">
                <SnButton text="키 발급" onClick={() => { onIssue(); }} />
                <SnButton text="비밀번호 교체" onClick={() => { onRotate(); }} />
              </div>
            }
          ></SharedForm>
        </SnResizeBar>
      </div>
    </div>
  );
});

export default SN_SAI_CUS_REG_001;
